// src/pages/PaymentDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getPartnerOptions, getPartnerLabel } from "../utils/partnerMap";
import { useAuth } from "../auth/AuthContext";

// ✅ use centralized service (no BASE_URL, no fetch here)
import {
	getPaymentDetails,
	submitPaymentInfo
} from "../utils/paymentApiService";

// ✅ reuse same cache provider (sessionStorage lifecycle)
import { useDashboardCache } from "../utils/DashboardCacheContext";

/* ----------------------------- UI HELPERS ----------------------------- */
const Badge = ({ text, tone = "slate" }) => {
	const toneMap = {
		slate: "bg-slate-100 text-slate-700 border-slate-200",
		amber: "bg-amber-50 text-amber-700 border-amber-200",
		emerald: "bg-emerald-50 text-emerald-700 border-emerald-200"
	};
	return (
		<span
			className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
				toneMap[tone] || toneMap.slate
			}`}
		>
			{text}
		</span>
	);
};

const EmptyState = ({
	title = "No Payment Info Found",
	subtitle = "Try selecting another partner or check back later."
}) => (
	<div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-10">
		<div className="flex items-center justify-center">
			<div className="max-w-2xl text-center">
				<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-600">
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
							stroke="currentColor"
							strokeWidth="2"
						/>
						<path
							d="M12 16V12"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
						/>
						<path
							d="M12 8H12.01"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
						/>
					</svg>
				</div>

				<h3 className="text-3xl font-light tracking-wide text-slate-700">
					{title}
				</h3>
				<p className="mt-2 text-sm text-slate-500">{subtitle}</p>
			</div>
		</div>
	</div>
);

const FieldLabel = ({ children }) => (
	<label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
		{children}
	</label>
);

const ReadOnlyRow = ({ label, value }) => (
	<div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2">
		<div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
			{label}
		</div>
		<div className="text-right text-sm font-medium text-slate-900">{value}</div>
	</div>
);

/* ----------------------------- EDIT MODAL (OUTSIDE ✅) ----------------------------- */
const EditPaymentModal = React.memo(function EditPaymentModal({
	isOpen,
	isAdmin,
	editingRow,
	editDeductionInput,
	setEditDeductionInput,
	editInvoiceStatus,
	setEditInvoiceStatus,
	editPaymentStatus,
	setEditPaymentStatus,
	isSaving,
	onSave,
	onClose
}) {
	if (!isOpen || !isAdmin || !editingRow) return null;

	const actual = Number(editingRow.actualRevenue || 0);
	const deductionNum = Number(editDeductionInput || 0) || 0;
	const computedAfter = actual - deductionNum; // ✅ allow negative

	return (
		<div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center">
			<div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
				<div className="mb-3 flex items-center justify-between">
					<div className="text-sm font-semibold text-slate-900">
						Edit Payment
					</div>
					<span className="text-xs text-slate-500">
						{editingRow.paymentMonth}
					</span>
				</div>

				<div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
					<ReadOnlyRow
						label="Partner"
						value={getPartnerLabel(editingRow.partnerKey).toUpperCase()}
					/>
					<ReadOnlyRow
						label="Payment Month"
						value={editingRow.paymentMonth}
					/>
					<ReadOnlyRow
						label="Actual Revenue"
						value={`$${actual}`}
					/>
					<ReadOnlyRow
						label="Revenue After Deduction"
						value={`$${computedAfter}`}
					/>
				</div>

				<div className="mt-4 space-y-4">
					<div>
						<FieldLabel>Deduction Amount ($)</FieldLabel>
						<div className="flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:border-slate-500">
							<span className="mr-2 text-sm text-slate-500">$</span>
							<input
								type="text"
								inputMode="numeric"
								pattern="[0-9]*"
								value={editDeductionInput}
								onChange={(e) => {
									const v = e.target.value.replace(/[^\d]/g, "");
									setEditDeductionInput(v);
								}}
								onBlur={() => {
									if (editDeductionInput === "") setEditDeductionInput("0");
								}}
								className="w-full text-sm text-slate-900 outline-none"
							/>
						</div>
					</div>

					<div>
						<FieldLabel>Invoice Status</FieldLabel>
						<select
							className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
							value={editInvoiceStatus}
							onChange={(e) => setEditInvoiceStatus(e.target.value)}
						>
							<option value="PENDING">PENDING</option>
							<option value="RECEIVED">RECEIVED</option>
						</select>
					</div>

					<div>
						<FieldLabel>Payment Status</FieldLabel>
						<select
							className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
							value={editPaymentStatus}
							onChange={(e) => setEditPaymentStatus(e.target.value)}
						>
							<option value="PENDING">PENDING</option>
							<option value="PAID">PAID</option>
						</select>
					</div>

					<button
						type="button"
						onClick={onSave}
						disabled={isSaving}
						className={`w-full rounded-lg px-4 py-3 text-sm font-semibold text-white shadow transition ${
							isSaving
								? "cursor-not-allowed bg-slate-400"
								: "bg-emerald-500 hover:bg-emerald-600"
						}`}
					>
						{isSaving ? "SAVING..." : "SAVE"}
					</button>

					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						CLOSE
					</button>

					<p className="text-xs text-slate-500">
						Only Deduction, Invoice Status, and Payment Status are editable.
					</p>
				</div>
			</div>
		</div>
	);
});

/* ----------------------------- COMPONENT ----------------------------- */
const PaymentDetails = () => {
	const auth = useAuth?.() || {};
	const role = String(auth?.role || "").toLowerCase();
	const isAdmin = role === "fl-admin";

	const token = auth?.token;
	const partnerFromAuth = auth?.partnerName; // partner alias

	// ✅ cache access (sessionStorage)
	const { get: cacheGet, set: cacheSet } = useDashboardCache();
	const PAYMENT_CACHE_ROOT_KEY = "payment_cache_v1"; // stored inside dash_cache_v1 store

	// Admin selects partner. Partner user cannot.
	const partnerKeys = useMemo(() => getPartnerOptions() || [], []);
	const [selectedPartnerKey, setSelectedPartnerKey] = useState("");
	const effectivePartnerKey = isAdmin ? selectedPartnerKey : partnerFromAuth;

	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	// edit modal state (admin only)
	const [editingRow, setEditingRow] = useState(null);

	// ✅ FIX: string state for stable typing
	const [editDeductionInput, setEditDeductionInput] = useState("0");
	const [editInvoiceStatus, setEditInvoiceStatus] = useState("PENDING");
	const [editPaymentStatus, setEditPaymentStatus] = useState("PENDING");
	const [isSaving, setIsSaving] = useState(false);

	const invoiceTone = (s) =>
		String(s || "").toUpperCase() === "RECEIVED" ? "emerald" : "amber";
	const payTone = (s) =>
		String(s || "").toUpperCase() === "PAID" ? "emerald" : "amber";

	const mapApiItemToRow = (item, partnerKeyFallback) => {
		const paymentMonth = item?.paymentMonth || item?.sk?.replace("MONTH#", "");
		const partnerKey = item?.partnerName || partnerKeyFallback || "";

		return {
			id: item?.sk
				? `${item?.pk || ""}#${item?.sk}`
				: `${partnerKey}#${paymentMonth}`,
			partnerKey,
			paymentMonth,
			actualRevenue: Number(item?.actualRevenue ?? 0),
			deductionAmount: Number(item?.deductionAmount ?? 0),
			revenueAfterDeduction: Number(
				item?.afterDeduction ?? item?.revenueAfterDeduction ?? 0
			),
			invoiceStatus: String(item?.invoiceStatus || "PENDING").toUpperCase(),
			paymentStatus: String(item?.paymentStatus || "PENDING").toUpperCase()
		};
	};

	// ✅ helpers to read/write payment cache (per partner) without touching dashboard cache
	const readPaymentCache = (partnerAlias) => {
		const root = cacheGet(PAYMENT_CACHE_ROOT_KEY);
		const data = root?.data && typeof root.data === "object" ? root.data : {};
		return data?.[partnerAlias] ?? null;
	};

	const writePaymentCache = (partnerAlias, value) => {
		const root = cacheGet(PAYMENT_CACHE_ROOT_KEY);
		const data = root?.data && typeof root.data === "object" ? root.data : {};
		const next = { ...data, [partnerAlias]: value };
		cacheSet(PAYMENT_CACHE_ROOT_KEY, next);
	};

	const deletePaymentCache = (partnerAlias) => {
		const root = cacheGet(PAYMENT_CACHE_ROOT_KEY);
		const data = root?.data && typeof root.data === "object" ? root.data : {};
		if (!data?.[partnerAlias]) return;
		const next = { ...data };
		delete next[partnerAlias];
		cacheSet(PAYMENT_CACHE_ROOT_KEY, next);
	};

	const load = async (partnerAlias) => {
		setErrorMsg("");
		setEditingRow(null);

		// ✅ 1) Use cache if available (no TTL: keep until tab closed)
		const cachedRows = readPaymentCache(partnerAlias);
		if (Array.isArray(cachedRows)) {
			setRows(cachedRows);
			return;
		}

		// ✅ 2) Fetch if no cache
		setLoading(true);
		setRows([]);
		try {
			const items = await getPaymentDetails({ partnerAlias, token });
			const normalized = (Array.isArray(items) ? items : []).map((it) =>
				mapApiItemToRow(it, partnerAlias)
			);

			setRows(normalized);

			// ✅ store cache (until tab closed)
			writePaymentCache(partnerAlias, normalized);
		} catch (e) {
			console.error(e);
			setErrorMsg(e?.message || "Failed to load payment info.");
			setRows([]);
		} finally {
			setLoading(false);
		}
	};

	// Auto-load
	useEffect(() => {
		if (!token) {
			setRows([]);
			return;
		}

		if (!effectivePartnerKey) {
			setRows([]);
			setEditingRow(null);
			return;
		}

		load(effectivePartnerKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectivePartnerKey, token, isAdmin]);

	const onPartnerChangeAdmin = (e) => {
		setSelectedPartnerKey(e.target.value);
	};

	const openEdit = (row) => {
		if (!isAdmin) return;
		setEditingRow(row);

		setEditDeductionInput(String(Number(row?.deductionAmount || 0)));
		setEditInvoiceStatus(String(row?.invoiceStatus || "PENDING").toUpperCase());
		setEditPaymentStatus(String(row?.paymentStatus || "PENDING").toUpperCase());
	};

	const closeEdit = () => {
		setEditingRow(null);
		setIsSaving(false);
	};

	const onSaveEdit = async () => {
		if (!isAdmin || !editingRow) return;

		const deductionNum = Number(editDeductionInput || 0) || 0;
		const actual = Number(editingRow.actualRevenue || 0);
		const revenueAfter = actual - deductionNum; // ✅ allow negative

		setIsSaving(true);
		try {
			const payload = {
				paymentMonth: editingRow.paymentMonth,
				actualRevenue: actual,
				deductionAmount: deductionNum,
				invoiceStatus: String(editInvoiceStatus || "PENDING").toUpperCase(),
				paymentStatus: String(editPaymentStatus || "PENDING").toUpperCase()
			};

			await submitPaymentInfo({
				partnerAlias: editingRow.partnerKey || effectivePartnerKey,
				token,
				payload
			});

			// ✅ update UI immediately
			setRows((prev) =>
				prev.map((r) =>
					r.id === editingRow.id
						? {
								...r,
								deductionAmount: deductionNum,
								invoiceStatus: payload.invoiceStatus,
								paymentStatus: payload.paymentStatus,
								revenueAfterDeduction: revenueAfter
						  }
						: r
				)
			);

			// ✅ requirement: clear cache if admin edits payment details
			// (so next time it refetches fresh from API)
			deletePaymentCache(editingRow.partnerKey || effectivePartnerKey);

			closeEdit();
		} catch (e) {
			console.error(e);
			alert(e?.message || "Save failed. Please try again.");
			setIsSaving(false);
		}
	};

	return (
		<section className="rounded-xl bg-white p-5 shadow-sm">
			{/* ✅ Admin only: partner dropdown */}
			{isAdmin ? (
				<div className="mb-4 max-w-[280px]">
					<label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
						Select Partner
					</label>
					<select
						className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500"
						value={selectedPartnerKey}
						onChange={onPartnerChangeAdmin}
					>
						<option value="">Select Partner</option>
						{partnerKeys.map((k) => (
							<option
								key={k}
								value={k}
							>
								{getPartnerLabel(k)}
							</option>
						))}
					</select>
				</div>
			) : null}

			{/* Content */}
			{loading ? (
				<div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-10">
					<div className="flex items-center justify-center">
						<div className="flex items-center gap-3 text-slate-700">
							<span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
							<span className="text-sm font-medium">
								Loading payment info...
							</span>
						</div>
					</div>
				</div>
			) : errorMsg ? (
				<div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
					{errorMsg}
				</div>
			) : isAdmin && !selectedPartnerKey ? (
				<EmptyState
					title="Select a Partner"
					subtitle="Admin can choose a partner from the dropdown to view payment history."
				/>
			) : !isAdmin && !effectivePartnerKey ? (
				<EmptyState
					title="No Partner Found"
					subtitle="Your account doesn’t have a partner assigned in AuthContext yet."
				/>
			) : rows.length === 0 ? (
				<EmptyState
					title="No Payment Info Found"
					subtitle="No payment records exist for this partner yet."
				/>
			) : (
				<div className="overflow-hidden rounded-2xl border border-slate-200">
					<table className="w-full table-fixed text-left text-sm">
						<colgroup>
							<col className={isAdmin ? "w-[12%]" : "w-[14%]"} />
							<col className="w-[10%]" />
							<col className="w-[12%]" />
							<col className="w-[12%]" />
							<col className="w-[16%]" />
							<col className="w-[12%]" />
							<col className="w-[12%]" />
							{isAdmin ? <col className="w-[6%]" /> : null}
						</colgroup>

						<thead className="bg-gradient-to-b from-slate-900 to-slate-800 text-white text-center">
							<tr>
								<th className="px-3 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
									Partner Name
								</th>
								<th className="px-3 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
									Payment Month
								</th>
								<th className="px-3 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
									Actual Revenue ($)
								</th>
								<th className="px-3 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
									Deduction Amount ($)
								</th>
								<th className="px-3 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
									After Deduction ($)
								</th>
								<th className="px-3 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
									Invoice Status
								</th>
								<th className="px-3 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
									Payment Status
								</th>
								{isAdmin ? (
									<th className="px-2 py-3 text-[11px] font-semibold uppercase leading-4 tracking-wide">
										Edit
									</th>
								) : null}
							</tr>
						</thead>

						<tbody className="bg-white">
							{rows.map((r, idx) => {
								const isEven = idx % 2 === 0;
								const isActive = isAdmin && editingRow?.id === r.id;

								return (
									<tr
										key={r.id}
										className={`border-t border-slate-200 text-center ${
											isEven ? "bg-slate-50/60" : "bg-white"
										} hover:bg-slate-50`}
									>
										<td className="px-3 py-4 font-semibold text-slate-900">
											{getPartnerLabel(
												r.partnerKey || effectivePartnerKey
											).toUpperCase()}
										</td>
										<td className="px-3 py-4 text-slate-700">
											{r.paymentMonth}
										</td>
										<td className="px-3 py-4 text-slate-700">
											{r.actualRevenue}
										</td>
										<td className="px-3 py-4 text-slate-700">
											{r.deductionAmount}
										</td>
										<td className="px-3 py-4 font-semibold text-slate-900">
											{r.revenueAfterDeduction}
										</td>
										<td className="px-3 py-4">
											<Badge
												text={String(r.invoiceStatus).toUpperCase()}
												tone={invoiceTone(r.invoiceStatus)}
											/>
										</td>
										<td className="px-3 py-4">
											<Badge
												text={String(r.paymentStatus).toUpperCase()}
												tone={payTone(r.paymentStatus)}
											/>
										</td>

										{isAdmin ? (
											<td className="px-2 py-4">
												<button
													type="button"
													onClick={() => openEdit(r)}
													className={`inline-flex h-9 w-10 items-center justify-center rounded-lg border shadow-sm transition active:scale-[0.98]
														${
															isActive
																? "border-slate-900 bg-slate-900 text-white"
																: "border-slate-300 bg-white text-slate-900 hover:bg-slate-50"
														}`}
													title="Edit"
												>
													<svg
														width="18"
														height="18"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M12 20H21"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
														/>
														<path
															d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</button>
											</td>
										) : null}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* ✅ Edit modal (admin only) */}
			<EditPaymentModal
				isOpen={!!editingRow}
				isAdmin={isAdmin}
				editingRow={editingRow}
				editDeductionInput={editDeductionInput}
				setEditDeductionInput={setEditDeductionInput}
				editInvoiceStatus={editInvoiceStatus}
				setEditInvoiceStatus={setEditInvoiceStatus}
				editPaymentStatus={editPaymentStatus}
				setEditPaymentStatus={setEditPaymentStatus}
				isSaving={isSaving}
				onSave={onSaveEdit}
				onClose={closeEdit}
			/>
		</section>
	);
};

export default PaymentDetails;
